/**
 * ReturnInvoiceScreen — issue a credit note against a sale invoice.
 *
 *  ┌─── Header (back · original #) ─────────────────────────────┐
 *  ├─── Items table (qty stepper, "all" toggle) ──┬── Refund ───┤
 *  │                                              │ Method      │
 *  │                                              │ Notes       │
 *  │                                              │ Subtotal    │
 *  │                                              │ Tax         │
 *  │                                              │ Refund      │
 *  │                                              │ Confirm btn │
 *  └──────────────────────────────────────────────┴─────────────┘
 *
 * Tax is recomputed per line at confirm-time inside useCreateCreditNote
 * (single source of truth). The on-screen "refund" total mirrors that
 * math so what the cashier sees matches what gets written.
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, RotateCcw } from 'lucide-react';
import { database } from '@/db';
import { Invoice, LineItem } from '@/db/models/Billing';
import { Product } from '@/db/models/Inventory';
import { useObservable } from '@/hooks/useObservable';
import {
    useCreateCreditNote,
    type ReturnItem,
    type RefundMethod,
} from '@/billing/useCreateCreditNote';
import { loadRemainingReturnable } from '@/billing/returnLedgerDataSource';
import { isFullyReturned } from '@/billing/returnLedger';
import { computeReturnLine, toPaisa, toRupees } from '@/billing/billingEngine';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NumberField } from '@/components/ui/NumberField';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';

const REFUND_METHODS: { value: RefundMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
  { value: 'CREDIT', label: 'Credit (khata)' },
];

export function ReturnInvoiceScreen() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { createCreditNote, isCreating } = useCreateCreditNote();

  const [original, setOriginal] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const inv = await database.get<Invoice>('invoices').find(invoiceId);
        if (!cancelled) setOriginal(inv);
      } catch {
        if (!cancelled) setError('Could not load the original invoice.');
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  const lineItems = useObservable<LineItem[]>(
    () => {
      if (!invoiceId) return null;
      return database
        .get<LineItem>('line_items')
        .query(
          Q.where('invoice_id', invoiceId),
          Q.where('is_deleted', false),
        )
        .observe();
    },
    [invoiceId],
    [],
  );

  // Quantity-to-return state, keyed by line item id.
  const [qtyMap, setQtyMap] = useState<Record<string, number>>({});
  useEffect(() => {
    // Reset whenever the invoice changes; default to 0 (cashier opts in).
    setQtyMap(Object.fromEntries(lineItems.map((li) => [li.id, 0])));
  }, [lineItems]);

  /**
   * How much of each line can still come back.
   *
   * The cap used to be the quantity SOLD, which ignored earlier returns — the
   * same goods could be refunded and restocked over and over. `null` while the
   * ledger loads, so the screen does not briefly offer the full quantity.
   */
  const [remaining, setRemaining] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await loadRemainingReturnable(database, invoiceId);
        if (!cancelled) setRemaining(r);
      } catch {
        if (!cancelled) setError('Could not check what has already been returned.');
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId, lineItems]);

  const maxFor = (lineItemId: string) => remaining?.[lineItemId] ?? 0;
  const nothingLeft = remaining !== null && lineItems.length > 0 && isFullyReturned(remaining);

  // Product names
  const productIds = useMemo(
    () => Array.from(new Set(lineItems.map((li) => li.productId))),
    [lineItems],
  );
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (productIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const map: Record<string, string> = {};
      for (let i = 0; i < productIds.length; i += 500) {
        const batch = productIds.slice(i, i + 500);
        const rows = await database
          .get<Product>('products')
          .query(Q.where('id', Q.oneOf(batch)))
          .fetch();
        for (const p of rows) map[p.id] = p.name;
      }
      if (!cancelled) setProductNames(map);
    })();
    return () => { cancelled = true; };
  }, [productIds]);

  const [refundMethod, setRefundMethod] = useState<RefundMethod>('CASH');
  const [notes, setNotes] = useState('');

  // ── Selection helpers ──
  const setQty = (id: string, value: number, max: number) => {
    setQtyMap((m) => ({ ...m, [id]: Math.max(0, Math.min(max, value)) }));
  };
  const allSelected = lineItems.length > 0
    && lineItems.every((li) => (qtyMap[li.id] ?? 0) === maxFor(li.id))
    && !nothingLeft;
  const toggleAll = () => {
    if (allSelected) {
      setQtyMap(Object.fromEntries(lineItems.map((li) => [li.id, 0])));
    } else {
      // "All" means everything still returnable, not everything originally sold.
      setQtyMap(Object.fromEntries(lineItems.map((li) => [li.id, maxFor(li.id)])));
    }
  };

  // ── Computed refund total ──
  //
  // Runs the same computeReturnLine() the writer does, rather than a second
  // copy of the arithmetic. The copy had drifted: it credited qty x unitPrice
  // plus GST, ignoring the line's discount and the store's TAX_INCLUSIVE
  // convention, so the figure quoted to the cashier no longer matched the
  // refund that was actually issued.
  const summary = useMemo(() => {
    if (!original) return { count: 0, subtotal: 0, tax: 0, total: 0, finalTotal: 0 };
    let count = 0, subtotal = 0, tax = 0, total = 0;
    for (const li of lineItems) {
      const q = qtyMap[li.id] ?? 0;
      if (q <= 0) continue;
      count += q;
      const line = computeReturnLine({
        quantityToReturn: q,
        originalQuantity: li.quantity,
        gstRate: li.gstRate,
        cessRate: li.cessRate,
        // Reverses what this line actually recorded. The current TAX_INCLUSIVE
        // setting is deliberately not consulted: a sale's tax was fixed when it
        // was made, so changing the setting afterwards must not move the refund.
        original: {
          taxableAmountPaisa: toPaisa(li.taxableAmount),
          cgstPaisa: toPaisa(li.cgstAmount),
          sgstPaisa: toPaisa(li.sgstAmount),
          igstPaisa: toPaisa(li.igstAmount),
          cessPaisa: toPaisa(li.cessAmount),
          totalPaisa: toPaisa(li.totalAmount),
          discountAmountPaisa: toPaisa(li.discountAmount ?? 0),
        },
      });
      subtotal += toRupees(line.taxableAmountPaisa);
      tax += toRupees(line.cgstPaisa + line.sgstPaisa + line.igstPaisa + line.cessPaisa);
      total += toRupees(line.totalPaisa);
    }
    return { count, subtotal, tax, total, finalTotal: Math.round(total) };
  }, [lineItems, qtyMap, original]);

  async function handleConfirm() {
    if (!original || !invoiceId) return;
    if (summary.count === 0) {
      setError('Select at least one item to return.');
      return;
    }
    setError(null);
    const items: ReturnItem[] = [];
    for (const li of lineItems) {
      const q = qtyMap[li.id] ?? 0;
      if (q > 0) {
        items.push({
          lineItemId: li.id,
          productId: li.productId,
          quantityToReturn: q,
          unitPrice: li.unitPrice,
          hsnCode: li.hsnCode,
          unit: li.unit,
          gstRate: li.gstRate,
          cessRate: li.cessRate,
        });
      }
    }
    const res = await createCreditNote(invoiceId, items, summary.finalTotal, refundMethod, notes);
    if (res.success && res.invoiceId) {
      notifySuccess(`Return saved — credit note ${res.invoiceNumber ?? ''} created.`.replace(' created', ' created').replace('  ', ' '));
      navigate(`/invoices/${res.invoiceId}`, { replace: true });
    } else {
      setError(res.error || 'Failed to create credit note');
    }
  }

  if (error && !original) {
    return (
      <div className="flex h-full items-center justify-center text-body text-danger-ink">{error}</div>
    );
  }
  if (!original) {
    return (
      <div className="flex h-full items-center justify-center text-body text-fg-tertiary">
        Loading invoice…
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(`/invoices/${original.id}`)}
          className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors"
        >
          <ArrowLeft size={14} /> Back to invoice
        </button>
        <div className="text-label uppercase text-fg-tertiary">
          Return against {original.invoiceNumber}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-auto lg:grid-cols-[1fr_360px] lg:overflow-visible">
        {/* Items */}
        <div className="min-h-0 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
          {/* Phones: Sold and Unit price fold into the item cell, so the
              return stepper stays on screen without sideways scrolling. */}
          <table className="fit-mobile w-full text-body">
            <thead className="sticky top-0 bg-bg-surface text-label uppercase text-fg-tertiary">
              <tr className="border-b border-stroke-hairline/10">
                <th className="px-4 py-2 text-left font-normal">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-label uppercase text-accent-ink hover:underline"
                  >
                    {allSelected ? 'Clear all' : 'Return all'}
                  </button>
                </th>
                <th className="hidden px-4 py-2 text-right font-normal sm:table-cell">Sold</th>
                <th className="hidden px-4 py-2 text-right font-normal sm:table-cell">Unit price</th>
                <th className="px-4 py-2 text-center font-normal">Return qty</th>
                <th className="px-4 py-2 text-right font-normal">Refund</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li) => {
                const q = qtyMap[li.id] ?? 0;
                const refund = q * li.unitPrice * (1 + (li.gstRate + li.cessRate) / 100);
                return (
                  <tr
                    key={li.id}
                    className={cn(
                      'border-b border-stroke-hairline/10',
                      q > 0 && 'bg-accent/5',
                    )}
                  >
                    <td className="px-4 py-2.5">
                      <div className="text-fg-primary">{li.description?.trim() || productNames[li.productId] || '…'}</div>
                      {li.hsnCode && (
                        <div className="text-label uppercase text-fg-tertiary">HSN {li.hsnCode}</div>
                      )}
                      <div className="text-label text-fg-tertiary tabular-nums sm:hidden">
                        Sold {li.quantity} {li.unit} × {formatCurrency(li.unitPrice)}
                      </div>
                    </td>
                    <td className="hidden px-4 py-2.5 text-right text-fg-secondary tabular-nums sm:table-cell">
                      {li.quantity} {li.unit}
                    </td>
                    <td className="hidden px-4 py-2.5 text-right text-fg-secondary tabular-nums sm:table-cell">
                      {formatCurrency(li.unitPrice)}
                    </td>
                    <td className="px-4 py-2.5">
                      <QtyStepper
                        value={q}
                        max={maxFor(li.id)}
                        onChange={(v) => setQty(li.id, v, maxFor(li.id))}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right text-fg-primary tabular-nums">
                      {q > 0 ? formatCurrency(refund) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Refund summary */}
        <aside className="flex min-h-0 flex-col rounded-lg border border-stroke-hairline/10 bg-bg-surface">
          <div className="border-b border-stroke-hairline/10 px-4 py-3">
            <div className="text-heading text-fg-primary">Refund</div>
            <div className="text-label uppercase text-fg-tertiary">
              {summary.count} {summary.count === 1 ? 'item' : 'items'} selected
            </div>
          </div>

          <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
            <div className="space-y-1.5">
              <Row label="Subtotal" value={formatCurrency(summary.subtotal)} muted />
              {summary.tax > 0 && <Row label="Tax" value={formatCurrency(summary.tax)} muted />}
              {summary.finalTotal !== summary.total && (
                <Row label="Round off" value={formatCurrency(summary.finalTotal - summary.total)} muted />
              )}
              <div className="flex items-baseline justify-between border-t border-stroke-hairline/10 pt-2">
                <span className="text-heading text-fg-primary">Total refund</span>
                <span className="text-display text-fg-primary tabular-nums">
                  {formatCurrency(summary.finalTotal)}
                </span>
              </div>
            </div>

            <div>
              <div className="text-label uppercase text-fg-tertiary">Refund via</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {REFUND_METHODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setRefundMethod(m.value)}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-body transition-colors',
                      refundMethod === m.value
                        ? 'btn-brand border border-white/10'
                        : 'bg-bg-subtle text-fg-secondary hover:bg-stroke-hairline/10 hover:text-fg-primary',
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-label uppercase text-fg-tertiary">Notes</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional"
                className="mt-1"
              />
            </div>

          </div>

          <div className="border-t border-stroke-hairline/10 px-4 py-3">
            <Button
              block
              size="lg"
              onClick={handleConfirm}
              disabled={isCreating || summary.count === 0}
            >
              <RotateCcw size={14} className="mr-1.5" />
              {isCreating ? 'Creating credit note…' : `Refund ${formatCurrency(summary.finalTotal)}`}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function QtyStepper({
  value,
  max,
  onChange,
}: {
  value: number;
  max: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="inline-flex items-center justify-center gap-0 mx-auto">
      <div className="inline-flex items-center rounded-md border border-stroke-hairline/15 bg-bg-canvas">
        <button
          type="button"
          onClick={() => onChange(value - 1)}
          disabled={value <= 0}
          className="flex h-7 w-7 items-center justify-center text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary disabled:opacity-30 transition-colors"
          aria-label="Decrease"
        >
          <Minus size={12} />
        </button>
        {/* 0 is a real answer here — "return none of this line" — so min stays
            0, but an empty or half-typed field still commits nothing. */}
        <NumberField
          aria-label="Return quantity"
          value={value}
          onCommit={onChange}
          commitOn="blur"
          min={0}
          max={max}
          className="h-7 w-12 bg-transparent text-center text-body text-fg-primary outline-none"
        />
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          disabled={value >= max}
          className="flex h-7 w-7 items-center justify-center text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary disabled:opacity-30 transition-colors"
          aria-label="Increase"
        >
          <Plus size={12} />
        </button>
      </div>
      <span className="ml-2 text-label uppercase text-fg-tertiary tabular-nums">/ {max}</span>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between text-body">
      <span className={muted ? 'text-fg-tertiary' : 'text-fg-secondary'}>{label}</span>
      <span className={cn('tabular-nums', muted ? 'text-fg-secondary' : 'text-fg-primary')}>{value}</span>
    </div>
  );
}
